import type { FilterOptions } from '@/components/filter/filter-types';

export const highlightSearchterm = (searchStringList: string[], resultString: string) => {
  const sortedSearchStringList = searchStringList
    .map((t) => t.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')) // Escape special characters
    .sort((a, b) => b.length - a.length); // Sort inputTokens by length, in descending order

  return resultString
    .split(/\s/)
    .map((word) => {
      const filteredSearchString = sortedSearchStringList.filter((input) =>
        new RegExp(input, 'i').test(word)
      );
      if (filteredSearchString.length === 0) {
        return word;
      }

      return word.replace(
        new RegExp(filteredSearchString[0], 'i'),
        (input) => `<strong>${input}</strong>`
      );
    })
    .join(' ');
};

export const getMatchCount = (label: string, tokens: string[]) => {
  const normalizedLabel = label.toLowerCase().replace(/\s+/g, '');
  return tokens.reduce((count, token) => count + (normalizedLabel.includes(token) ? 1 : 0), 0);
};

export const filteredOptions = (value: string, options: FilterOptions[], groupEntries = false) => {
  const trimmedInput = value.trim();
  let inputTokens = trimmedInput
    .toLowerCase()
    .split(/\s+/)
    .filter((s: string) => s.length > 0);

  if (inputTokens.length === 0) return options;

  let filterOptions = options;
  let foundGroup = false;

  // check if we have a 100% match on the groupName.
  if (groupEntries) {
    const matchingGroups = options.filter((option) => {
      const groupName = (option.groupName || '').toLowerCase();

      const compressedLabel = groupName.replace(/\s+/g, '');
      // Check for a 100% match on the groupName ("apple iphone" will only search Apple)
      return inputTokens.includes(groupName) || inputTokens.includes(compressedLabel);
    });

    // When we have a groupNameMatch, use this options for further filtering
    if (matchingGroups?.length > 0) {
      foundGroup = true;
      filterOptions = matchingGroups;
      // remove group name from inputToken-filter
      inputTokens = inputTokens.filter(
        (token) => token !== matchingGroups[0].groupName?.toLowerCase()
      );
    }
  }

  if (inputTokens.length === 0) return filterOptions;

  return filterOptions
    .filter((option) => {
      // When we have already filtered for the groupName, we do not want to add it to the search.
      const groupName = !foundGroup ? (option.groupName || '').toLowerCase() : '';

      const normalizedLabel = `${groupName} ${option.label}`.toLowerCase(); // Add "group" to search. It could contain the Brand.
      const compressedLabel = normalizedLabel.replace(/\s+/g, '');
      return (
        inputTokens.some((token) => normalizedLabel.includes(token)) ||
        inputTokens.some((token) => compressedLabel.includes(token))
      );
    })
    .sort((a, b) => {
      // First, sort by group alphabetically
      if (groupEntries && a.groupName && b.groupName) {
        if (a.groupName?.toLowerCase() < b.groupName?.toLowerCase()) return -1;
        if (a.groupName?.toLowerCase() > b.groupName?.toLowerCase()) return 1;
      }

      const aExactMatch = a.label.toLowerCase() === trimmedInput.toLowerCase();
      const bExactMatch = b.label.toLowerCase() === trimmedInput.toLowerCase();

      if (aExactMatch) return -1;
      if (bExactMatch) return 1;

      // sort the results by token match count (2 words matched, 3 words matched etc)
      const aMatchCount = getMatchCount(a.label, inputTokens);
      const bMatchCount = getMatchCount(b.label, inputTokens);
      return bMatchCount - aMatchCount;
    })
    .map((item) => ({
      ...item,
      label: highlightSearchterm(inputTokens, item.label),
    }));
};
